/**
 * MyPagePanel - 마이페이지 패널
 * 프로필 관리, 비밀번호 변경, 학교 통계, 개인정보 관리
 */

import { supabaseManager } from '../../core/SupabaseManager.js';
import { consentManager } from '../../core/ConsentManager.js';
import { privacyPolicyPanel, PRIVACY_POLICY_CONTENT } from './PrivacyPolicyPanel.js';
import { REGIONS, normalizeRegion } from './regions.js';

class MyPagePanel {
  constructor() {
    this.modal = null;
    this.profile = null;
    this.schoolStatsPage = 1;
    this._schoolStats = null;
    this._mySchool = null;
    // 관리자 - 회원 목록
    this._members = null;
    this._membersPage = 1;
    this._memberSearch = '';
  }

  /**
   * 패널 열기
   */
  async show() {
    if (!supabaseManager.isLoggedIn()) {
      alert('로그인이 필요합니다.');
      return;
    }
    await this.render();
  }

  /**
   * 모달 렌더링
   */
  async render() {
    this.close();

    this.modal = document.createElement('div');
    this.modal.className = 'modal-overlay mypage-modal active';

    // 프로필 로드
    try {
      this.profile = await supabaseManager.getProfile();
    } catch (error) {
      console.error('프로필 로드 실패:', error);
      this.profile = null;
    }

    const user = supabaseManager.getUser();

    this.modal.innerHTML = `
      <div class="modal-content mypage-content">
        <div class="modal-header">
          <h3>마이페이지</h3>
          <button class="modal-close" id="mypage-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="mypage-tabs">
            <button class="mypage-tab active" data-tab="profile">프로필</button>
            <button class="mypage-tab" data-tab="password">비밀번호 변경</button>
            <button class="mypage-tab" data-tab="school-stats">학교 통계</button>
            <button class="mypage-tab" data-tab="privacy">개인정보 관리</button>
            ${supabaseManager.isAdmin() ? '<button class="mypage-tab" data-tab="admin">관리자 설정</button>' : ''}
          </div>

          <!-- 프로필 탭 -->
          <div class="mypage-tab-content active" id="profile-tab">
            <div class="profile-email">
              <label>이메일</label>
              <span>${user.email}</span>
            </div>
            <div class="form-group">
              <label for="profile-name">이름</label>
              <input type="text" id="profile-name" placeholder="이름을 입력하세요" value="${this.profile?.name || ''}">
            </div>
            <div class="form-group">
              <label for="profile-nickname">닉네임</label>
              <input type="text" id="profile-nickname" placeholder="닉네임을 입력하세요" value="${this.profile?.nickname || ''}">
            </div>

            <div class="school-registration-section">
              <h4>학교 등록</h4>
              ${this.profile?.school ? `
                <div class="current-school">
                  <span class="school-badge">${normalizeRegion(this.profile.region)} ${this.profile.school}</span>
                </div>
              ` : ''}
              <div class="form-group">
                <label for="profile-region">지역 (시/도)</label>
                <select id="profile-region">
                  <option value="">지역을 선택하세요</option>
                  ${REGIONS.map(r => `<option value="${r}" ${normalizeRegion(this.profile?.region) === r ? 'selected' : ''}>${r}</option>`).join('')}
                </select>
              </div>
              <div class="form-group">
                <label for="profile-school">학교명</label>
                <input type="text" id="profile-school" placeholder="공식 학교 명칭을 적어주세요" value="${this.profile?.school || ''}">
              </div>
            </div>

            <button class="btn btn-primary btn-full" id="save-profile-btn">프로필 저장</button>
          </div>

          <!-- 비밀번호 변경 탭 -->
          <div class="mypage-tab-content" id="password-tab" style="display:none;">
            <div class="form-group">
              <label for="new-password">새 비밀번호</label>
              <input type="password" id="new-password" placeholder="새 비밀번호 (6자 이상)">
            </div>
            <div class="form-group">
              <label for="confirm-password">비밀번호 확인</label>
              <input type="password" id="confirm-password" placeholder="비밀번호 확인">
            </div>
            <button class="btn btn-primary btn-full" id="change-password-btn">비밀번호 변경</button>
          </div>

          <!-- 학교 통계 탭 -->
          <div class="mypage-tab-content" id="school-stats-tab" style="display:none;">
            <div id="my-school-info" class="my-school-info">
              <div class="loading">학교 정보 로딩 중...</div>
            </div>
            <h4>학교별 사용자 순위</h4>
            <div id="school-ranking" class="school-ranking">
              <div class="loading">순위 로딩 중...</div>
            </div>
          </div>

          <!-- 개인정보 관리 탭 -->
          <div class="mypage-tab-content" id="privacy-tab" style="display:none;">
            <div class="privacy-management">
              <!-- 개인정보처리방침 -->
              <div class="privacy-section-card">
                <h4>개인정보처리방침</h4>
                <p class="privacy-description">e-GIS의 개인정보처리방침을 확인할 수 있습니다.</p>
                <button class="btn btn-secondary" id="view-privacy-policy">전문 보기</button>
              </div>

              <!-- 나의 동의 내역 -->
              <div class="privacy-section-card">
                <h4>나의 동의 내역</h4>
                <div id="consent-history" class="consent-history">
                  <div class="loading">동의 정보 로딩 중...</div>
                </div>
              </div>

              <!-- 개인정보 관리 -->
              <div class="privacy-section-card">
                <h4>개인정보 관리</h4>
                <div class="privacy-actions">
                  <div class="privacy-action-item">
                    <div class="action-info">
                      <strong>개인정보 열람</strong>
                      <p>저장된 개인정보를 확인합니다.</p>
                    </div>
                    <button class="btn btn-secondary btn-sm" id="view-my-data">열람</button>
                  </div>
                  <div class="privacy-action-item">
                    <div class="action-info">
                      <strong>개인정보 정정</strong>
                      <p>프로필 탭에서 정보를 수정할 수 있습니다.</p>
                    </div>
                    <button class="btn btn-secondary btn-sm" id="go-to-profile">프로필로 이동</button>
                  </div>
                  <div class="privacy-action-item danger">
                    <div class="action-info">
                      <strong>회원 탈퇴 (동의 철회)</strong>
                      <p>모든 데이터가 삭제되며 복구할 수 없습니다.</p>
                    </div>
                    <button class="btn btn-danger btn-sm" id="withdraw-consent">탈퇴하기</button>
                  </div>
                </div>
              </div>

              <!-- 개인정보 보호책임자 -->
              <div class="privacy-section-card">
                <h4>개인정보 보호책임자</h4>
                <div class="privacy-officer-info">
                  <div class="officer-item">
                    <span class="officer-label">담당자</span>
                    <span class="officer-value">김용현</span>
                  </div>
                  <div class="officer-item">
                    <span class="officer-label">이메일</span>
                    <a href="mailto:bgnlkim@gmail.com" class="officer-value">bgnlkim@gmail.com</a>
                  </div>
                </div>
                <p class="privacy-help-text">
                  개인정보 관련 문의사항이 있으시면 위 연락처로 문의해주세요.
                </p>
              </div>
            </div>
          </div>

          ${supabaseManager.isAdmin() ? `
          <!-- 관리자 설정 탭 -->
          <div class="mypage-tab-content" id="admin-tab" style="display:none;">
            <div class="admin-section">
              <div class="admin-members-header">
                <h4>가입 회원 목록 <span id="admin-members-count" class="admin-members-count"></span></h4>
                <button class="btn btn-secondary btn-sm" id="refresh-members-btn">새로고침</button>
              </div>
              <input type="text" id="admin-members-search" class="admin-members-search" placeholder="이름·이메일·닉네임·지역·학교 검색">
              <div id="admin-members" class="admin-members">
                <div class="loading">회원 목록 로딩 중...</div>
              </div>
            </div>
            <div class="admin-section">
              <h4>Supabase 설정</h4>
              <p class="admin-warning">주의: 설정을 초기화하면 다시 URL과 Key를 입력해야 합니다.</p>
              <button class="btn btn-danger" id="reset-supabase-btn">Supabase 설정 초기화</button>
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);
    this.bindEvents();
  }

  /**
   * 이벤트 바인딩
   */
  bindEvents() {
    // 닫기 버튼
    const closeBtn = document.getElementById('mypage-close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    // 탭 전환
    const tabs = this.modal.querySelectorAll('.mypage-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab.dataset.tab));
    });

    // 프로필 저장
    const saveProfileBtn = document.getElementById('save-profile-btn');
    if (saveProfileBtn) {
      saveProfileBtn.addEventListener('click', () => this.saveProfile());
    }

    // 비밀번호 변경
    const changePasswordBtn = document.getElementById('change-password-btn');
    if (changePasswordBtn) {
      changePasswordBtn.addEventListener('click', () => this.changePassword());
    }

    // Supabase 초기화 (관리자 전용)
    const resetSupabaseBtn = document.getElementById('reset-supabase-btn');
    if (resetSupabaseBtn) {
      resetSupabaseBtn.addEventListener('click', () => this.resetSupabase());
    }

    // 회원 목록 새로고침 (관리자 전용)
    const refreshMembersBtn = document.getElementById('refresh-members-btn');
    if (refreshMembersBtn) {
      refreshMembersBtn.addEventListener('click', () => this.loadMembers());
    }

    // 회원 목록 검색 (관리자 전용)
    const membersSearch = document.getElementById('admin-members-search');
    if (membersSearch) {
      membersSearch.addEventListener('input', (e) => {
        this._memberSearch = e.target.value;
        this._membersPage = 1;
        this.renderMembers();
      });
    }

    // 개인정보 관리 탭 이벤트
    const viewPrivacyBtn = document.getElementById('view-privacy-policy');
    if (viewPrivacyBtn) {
      viewPrivacyBtn.addEventListener('click', () => privacyPolicyPanel.show());
    }

    const viewMyDataBtn = document.getElementById('view-my-data');
    if (viewMyDataBtn) {
      viewMyDataBtn.addEventListener('click', () => this.showMyData());
    }

    const goToProfileBtn = document.getElementById('go-to-profile');
    if (goToProfileBtn) {
      goToProfileBtn.addEventListener('click', () => this.switchTab('profile'));
    }

    const withdrawConsentBtn = document.getElementById('withdraw-consent');
    if (withdrawConsentBtn) {
      withdrawConsentBtn.addEventListener('click', () => this.handleWithdrawConsent());
    }
  }

  /**
   * Supabase 설정 초기화 (관리자 전용)
   */
  resetSupabase() {
    if (confirm('정말로 Supabase 설정을 초기화하시겠습니까?\n초기화 후 다시 URL과 Key를 입력해야 합니다.')) {
      localStorage.removeItem('eGIS_supabaseUrl');
      localStorage.removeItem('eGIS_supabaseKey');
      alert('Supabase 설정이 초기화되었습니다. 페이지를 새로고침합니다.');
      window.location.reload();
    }
  }

  /**
   * 탭 전환
   */
  switchTab(tabName) {
    this.modal.querySelectorAll('.mypage-tab').forEach(t => t.classList.remove('active'));
    this.modal.querySelector(`[data-tab="${tabName}"]`).classList.add('active');

    // 관리자(회원 목록) 탭에서만 모달을 넓혀 가로 스크롤 방지
    const content = this.modal.querySelector('.mypage-content');
    if (content) content.classList.toggle('wide', tabName === 'admin');

    this.modal.querySelectorAll('.mypage-tab-content').forEach(c => {
      c.style.display = 'none';
      c.classList.remove('active');
    });

    const tabContent = document.getElementById(`${tabName}-tab`);
    if (tabContent) {
      tabContent.style.display = 'block';
      tabContent.classList.add('active');
    }

    // 학교 통계 탭 선택 시 데이터 로드
    if (tabName === 'school-stats') {
      this.loadSchoolStats();
    }

    // 개인정보 관리 탭 선택 시 동의 정보 로드
    if (tabName === 'privacy') {
      this.loadConsentHistory();
    }

    // 관리자 설정 탭 선택 시 회원 목록 로드
    if (tabName === 'admin') {
      this.loadMembers();
    }
  }

  /**
   * 프로필 저장
   */
  async saveProfile() {
    const name = document.getElementById('profile-name').value.trim();
    const nickname = document.getElementById('profile-nickname').value.trim();
    const region = document.getElementById('profile-region').value;
    const school = document.getElementById('profile-school').value.trim();

    if (school && !region) {
      alert('학교를 등록하려면 지역을 선택해주세요.');
      return;
    }

    try {
      await supabaseManager.saveProfile({ name, nickname, region, school });
      alert('프로필이 저장되었습니다.');
      this.profile = { name, nickname, region, school };
      this.render(); // 화면 갱신
    } catch (error) {
      alert('프로필 저장 실패: ' + error.message);
    }
  }

  /**
   * 비밀번호 변경
   */
  async changePassword() {
    const newPassword = document.getElementById('new-password').value;
    const confirmPassword = document.getElementById('confirm-password').value;

    if (!newPassword || newPassword.length < 6) {
      alert('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      await supabaseManager.updatePassword(newPassword);
      alert('비밀번호가 변경되었습니다.');
      document.getElementById('new-password').value = '';
      document.getElementById('confirm-password').value = '';
    } catch (error) {
      alert('비밀번호 변경 실패: ' + error.message);
    }
  }

  /**
   * 학교 통계 로드
   */
  async loadSchoolStats() {
    const mySchoolInfo = document.getElementById('my-school-info');
    const schoolRanking = document.getElementById('school-ranking');

    try {
      // 내 학교 정보
      const mySchool = await supabaseManager.getMySchoolCount();
      if (mySchool) {
        mySchoolInfo.innerHTML = `
          <div class="my-school-card">
            <div class="my-school-name">${mySchool.school}</div>
            <div class="my-school-stats">
              <div class="stat-item">
                <span class="stat-value">${mySchool.count}</span>
                <span class="stat-label">명 사용 중</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">${mySchool.rank}</span>
                <span class="stat-label">위 / ${mySchool.totalSchools}개 학교</span>
              </div>
            </div>
          </div>
        `;
      } else {
        mySchoolInfo.innerHTML = `
          <div class="no-school-info">
            학교를 등록하면 통계를 확인할 수 있습니다.
          </div>
        `;
      }

      // 학교 순위
      const stats = await supabaseManager.getSchoolStats();
      this._schoolStats = stats;
      this._mySchool = mySchool;
      this.schoolStatsPage = 1;
      this.renderSchoolRanking();

    } catch (error) {
      console.error('학교 통계 로드 실패:', error);
      mySchoolInfo.innerHTML = '<div class="error">정보를 불러올 수 없습니다.</div>';
      schoolRanking.innerHTML = '<div class="error">순위를 불러올 수 없습니다.</div>';
    }
  }

  /**
   * 학교 순위 렌더링 (10위 단위 페이지네이션, 동석차 반영)
   */
  renderSchoolRanking() {
    const schoolRanking = document.getElementById('school-ranking');
    if (!schoolRanking) return;

    const stats = this._schoolStats || [];
    const mySchool = this._mySchool;

    if (stats.length === 0) {
      schoolRanking.innerHTML = '<div class="empty-list">등록된 학교가 없습니다.</div>';
      return;
    }

    const pageSize = 10;
    const totalPages = Math.ceil(stats.length / pageSize);
    const page = Math.min(Math.max(1, this.schoolStatsPage || 1), totalPages);
    this.schoolStatsPage = page;

    const start = (page - 1) * pageSize;
    const pageItems = stats.slice(start, start + pageSize);

    schoolRanking.innerHTML = `
      <div class="ranking-list">
        ${pageItems.map(item => `
          <div class="ranking-item ${item.rank <= 3 ? `rank-${item.rank}` : ''} ${mySchool && item.school === mySchool.school ? 'my-school' : ''}">
            <span class="ranking-position">${item.rank}</span>
            <span class="ranking-school">${item.school}</span>
            <span class="ranking-count">${item.count}명</span>
          </div>
        `).join('')}
      </div>
      ${totalPages > 1 ? `
        <div class="ranking-pagination">
          <button class="ranking-page-btn" data-page="prev" ${page === 1 ? 'disabled' : ''}>이전</button>
          <span class="ranking-page-info">${page} / ${totalPages}</span>
          <button class="ranking-page-btn" data-page="next" ${page === totalPages ? 'disabled' : ''}>다음</button>
        </div>
      ` : ''}
    `;

    schoolRanking.querySelectorAll('.ranking-page-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.page;
        if (dir === 'prev' && this.schoolStatsPage > 1) this.schoolStatsPage--;
        else if (dir === 'next' && this.schoolStatsPage < totalPages) this.schoolStatsPage++;
        else return;
        this.renderSchoolRanking();
      });
    });
  }

  /**
   * 가입 회원 목록 로드 (관리자 전용)
   */
  async loadMembers() {
    const container = document.getElementById('admin-members');
    if (!container) return;

    container.innerHTML = '<div class="loading">회원 목록 로딩 중...</div>';

    try {
      const members = await supabaseManager.listMembers();
      this._members = members;
      this._membersPage = 1;
      this.renderMembers();
    } catch (error) {
      console.error('회원 목록 로드 실패:', error);
      const raw = error?.message || '';
      // 서버 함수 미생성 시 안내
      const notCreated = /admin_list_members|does not exist|PGRST202|schema cache/i.test(raw);
      const msg = notCreated
        ? '회원 목록 함수(admin_list_members)가 아직 생성되지 않았습니다. supabase-admin-members.sql 을 Supabase SQL Editor에서 실행하세요.'
        : ('회원 목록을 불러올 수 없습니다: ' + raw);
      container.innerHTML = `<div class="error">${this.escapeHtml(msg)}</div>`;
    }
  }

  /**
   * 회원 목록 렌더링 (검색 + 20명 단위 페이지네이션)
   */
  renderMembers() {
    const container = document.getElementById('admin-members');
    const countEl = document.getElementById('admin-members-count');
    if (!container) return;

    const all = this._members || [];
    if (countEl) countEl.textContent = `총 ${all.length}명`;

    const q = (this._memberSearch || '').trim().toLowerCase();
    const filtered = q
      ? all.filter(m => [m.name, m.email, m.nickname, m.region, m.school]
          .some(v => (v || '').toLowerCase().includes(q)))
      : all;

    if (filtered.length === 0) {
      container.innerHTML = `<div class="empty-list">${all.length === 0 ? '가입한 회원이 없습니다.' : '검색 결과가 없습니다.'}</div>`;
      return;
    }

    const pageSize = 20;
    const totalPages = Math.ceil(filtered.length / pageSize);
    const page = Math.min(Math.max(1, this._membersPage || 1), totalPages);
    this._membersPage = page;

    const start = (page - 1) * pageSize;
    const pageItems = filtered.slice(start, start + pageSize);

    const cell = (v) => (v && String(v).trim())
      ? this.escapeHtml(v)
      : '<span class="cell-empty">-</span>';

    // 수정/탈퇴는 user_id가 있어야 대상을 지정할 수 있다.
    // 예전 버전 admin_list_members 는 user_id를 반환하지 않아 버튼이 조용히 안 먹었다 → 이유를 밝힌다.
    const hasUserId = all.some(m => m.user_id);
    const notice = hasUserId ? '' : `
      <div class="members-notice">
        회원 수정·탈퇴를 쓰려면 서버 함수를 갱신해야 합니다.
        Supabase SQL Editor에서 <code>supabase-admin-members.sql</code>을 실행한 뒤 새로고침하세요.
        (현재 목록에 회원 식별자(user_id)가 없어 대상 지정이 불가능합니다)
      </div>`;

    container.innerHTML = `
      ${notice}
      <div class="members-table-wrap">
        <table class="members-table">
          <thead>
            <tr>
              <th class="col-idx">#</th>
              <th>이름</th>
              <th>이메일</th>
              <th>닉네임</th>
              <th>지역</th>
              <th>학교</th>
              <th class="col-date">가입일</th>
              <th class="col-actions">관리</th>
            </tr>
          </thead>
          <tbody>
            ${pageItems.map((m, i) => `
              <tr>
                <td class="col-idx">${start + i + 1}</td>
                <td>${cell(m.name)}</td>
                <td class="col-email">${cell(m.email)}</td>
                <td>${cell(m.nickname)}</td>
                <td>${cell(m.region)}</td>
                <td>${cell(m.school)}</td>
                <td class="col-date">${this.formatMemberDate(m.created_at)}</td>
                <td class="col-actions">
                  <button class="member-action-btn edit" data-uid="${this.escapeHtml(m.user_id || '')}"
                    ${m.user_id ? '' : 'disabled title="서버 함수 갱신이 필요합니다"'}>수정</button>
                  <button class="member-action-btn danger" data-uid="${this.escapeHtml(m.user_id || '')}"
                    ${m.user_id ? '' : 'disabled title="서버 함수 갱신이 필요합니다"'}>탈퇴</button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ${totalPages > 1 ? `
        <div class="ranking-pagination">
          <button class="ranking-page-btn" data-mpage="prev" ${page === 1 ? 'disabled' : ''}>이전</button>
          <span class="ranking-page-info">${page} / ${totalPages}</span>
          <button class="ranking-page-btn" data-mpage="next" ${page === totalPages ? 'disabled' : ''}>다음</button>
        </div>
      ` : ''}
    `;

    container.querySelectorAll('.ranking-page-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.mpage;
        if (dir === 'prev' && this._membersPage > 1) this._membersPage--;
        else if (dir === 'next' && this._membersPage < totalPages) this._membersPage++;
        else return;
        this.renderMembers();
      });
    });

    // 회원 관리 버튼 (수정/탈퇴)
    const findMember = (uid) => (this._members || []).find(x => x.user_id === uid);
    container.querySelectorAll('.member-action-btn.edit').forEach(btn => {
      btn.addEventListener('click', () => {
        const m = findMember(btn.dataset.uid);
        if (m) this.openEditMember(m);
      });
    });
    container.querySelectorAll('.member-action-btn.danger').forEach(btn => {
      btn.addEventListener('click', () => {
        const m = findMember(btn.dataset.uid);
        if (m) this.confirmDeleteMember(m);
      });
    });
  }

  /**
   * 회원 정보 수정 모달 (관리자 전용)
   */
  openEditMember(member) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay member-edit-modal active';
    overlay.innerHTML = `
      <div class="modal-content member-edit-content">
        <div class="modal-header">
          <h3>회원 정보 수정</h3>
          <button class="modal-close" data-close>&times;</button>
        </div>
        <div class="modal-body">
          <div class="member-edit-email">${this.escapeHtml(member.email || '-')}</div>
          <div class="form-group">
            <label for="edit-member-name">이름</label>
            <input type="text" id="edit-member-name" value="${this.escapeHtml(member.name || '')}">
          </div>
          <div class="form-group">
            <label for="edit-member-nickname">닉네임</label>
            <input type="text" id="edit-member-nickname" value="${this.escapeHtml(member.nickname || '')}">
          </div>
          <div class="form-group">
            <label for="edit-member-region">지역 (시/도)</label>
            <select id="edit-member-region">
              <option value="">선택 안 함</option>
              ${REGIONS.map(r => `<option value="${r}" ${normalizeRegion(member.region) === r ? 'selected' : ''}>${r}</option>`).join('')}
            </select>
          </div>
          <div class="form-group">
            <label for="edit-member-school">학교</label>
            <input type="text" id="edit-member-school" value="${this.escapeHtml(member.school || '')}">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-close>취소</button>
          <button class="btn btn-primary" id="edit-member-save">저장</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', close));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    const saveBtn = overlay.querySelector('#edit-member-save');
    saveBtn.addEventListener('click', async () => {
      const name = overlay.querySelector('#edit-member-name').value.trim();
      const nickname = overlay.querySelector('#edit-member-nickname').value.trim();
      const region = overlay.querySelector('#edit-member-region').value;
      const school = overlay.querySelector('#edit-member-school').value.trim();

      if (school && !region) {
        alert('학교를 입력하려면 지역을 선택해주세요.');
        return;
      }

      saveBtn.disabled = true;
      saveBtn.textContent = '저장 중...';
      try {
        await supabaseManager.updateMemberProfile(member.user_id, { name, nickname, region, school });
        close();
        await this.loadMembers();
      } catch (err) {
        alert('수정 실패: ' + (err.message || ''));
        saveBtn.disabled = false;
        saveBtn.textContent = '저장';
      }
    });
  }

  /**
   * 회원 강제 탈퇴 확인 모달 (관리자 비밀번호 입력 필요)
   */
  confirmDeleteMember(member) {
    const label = member.name
      ? `${member.name} (${member.email || ''})`
      : (member.email || member.user_id);

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay member-delete-modal active';
    overlay.innerHTML = `
      <div class="modal-content member-delete-content">
        <div class="modal-header">
          <h3>회원 강제 탈퇴</h3>
          <button class="modal-close" data-close>&times;</button>
        </div>
        <div class="modal-body">
          <p class="member-delete-warning">
            <strong>${this.escapeHtml(label)}</strong> 회원의 계정과 모든 개인정보·프로젝트가
            <strong>영구 삭제</strong>됩니다. 이 작업은 <strong>되돌릴 수 없습니다.</strong>
          </p>
          <div class="form-group">
            <label for="delete-member-pw">확인을 위해 관리자 비밀번호를 입력하세요</label>
            <input type="password" id="delete-member-pw" placeholder="관리자 계정 비밀번호" autocomplete="current-password">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-close>취소</button>
          <button class="btn btn-danger" id="delete-member-confirm">영구 삭제</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', close));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    const pwInput = overlay.querySelector('#delete-member-pw');
    const confirmBtn = overlay.querySelector('#delete-member-confirm');
    setTimeout(() => pwInput.focus(), 0);

    const doDelete = async () => {
      const pw = pwInput.value;
      if (!pw) {
        alert('관리자 비밀번호를 입력하세요.');
        return;
      }

      confirmBtn.disabled = true;
      confirmBtn.textContent = '확인 중...';
      try {
        const ok = await supabaseManager.verifyPassword(pw);
        if (!ok) {
          alert('관리자 비밀번호가 일치하지 않습니다.');
          confirmBtn.disabled = false;
          confirmBtn.textContent = '영구 삭제';
          pwInput.value = '';
          pwInput.focus();
          return;
        }

        confirmBtn.textContent = '삭제 중...';
        await supabaseManager.deleteMember(member.user_id);
        close();
        await this.loadMembers();
        alert('회원이 완전히 삭제되었습니다.');
      } catch (err) {
        alert('삭제 실패: ' + (err.message || ''));
        confirmBtn.disabled = false;
        confirmBtn.textContent = '영구 삭제';
      }
    };

    confirmBtn.addEventListener('click', doDelete);
    pwInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); doDelete(); }
    });
  }

  /**
   * 가입일 포맷 (yyyy. m. d.)
   */
  formatMemberDate(value) {
    if (!value) return '<span class="cell-empty">-</span>';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '<span class="cell-empty">-</span>';
    return d.toLocaleDateString('ko-KR');
  }

  /**
   * HTML 이스케이프 (사용자 입력 값 안전 출력)
   */
  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  /**
   * 동의 내역 로드
   */
  async loadConsentHistory() {
    const consentHistoryDiv = document.getElementById('consent-history');
    if (!consentHistoryDiv) return;

    try {
      const consent = await consentManager.getUserConsent();

      if (consent) {
        consentHistoryDiv.innerHTML = `
          <div class="consent-info-card">
            <div class="consent-status">
              <span class="consent-badge active">동의함</span>
            </div>
            <div class="consent-details">
              <div class="consent-detail-item">
                <span class="detail-label">동의 항목</span>
                <span class="detail-value">개인정보 수집·이용 동의</span>
              </div>
              <div class="consent-detail-item">
                <span class="detail-label">동의 일시</span>
                <span class="detail-value">${consentManager.formatConsentDate(consent.privacy_consent_at)}</span>
              </div>
              <div class="consent-detail-item">
                <span class="detail-label">방침 버전</span>
                <span class="detail-value">${consent.privacy_policy_version || '-'}</span>
              </div>
            </div>
          </div>
        `;
      } else {
        consentHistoryDiv.innerHTML = `
          <div class="consent-info-card empty">
            <p>동의 내역을 찾을 수 없습니다.</p>
          </div>
        `;
      }
    } catch (error) {
      console.error('동의 내역 로드 실패:', error);
      consentHistoryDiv.innerHTML = '<div class="error">동의 정보를 불러올 수 없습니다.</div>';
    }
  }

  /**
   * 내 데이터 보기
   */
  async showMyData() {
    const user = supabaseManager.getUser();
    const profile = this.profile;

    let dataHtml = `
      <div class="my-data-modal">
        <h4>저장된 개인정보</h4>
        <div class="data-section">
          <h5>계정 정보</h5>
          <div class="data-item">
            <span class="data-label">이메일</span>
            <span class="data-value">${user?.email || '-'}</span>
          </div>
          <div class="data-item">
            <span class="data-label">가입일</span>
            <span class="data-value">${user?.created_at ? new Date(user.created_at).toLocaleDateString('ko-KR') : '-'}</span>
          </div>
        </div>
        <div class="data-section">
          <h5>프로필 정보</h5>
          <div class="data-item">
            <span class="data-label">이름</span>
            <span class="data-value">${profile?.name || '(미등록)'}</span>
          </div>
          <div class="data-item">
            <span class="data-label">닉네임</span>
            <span class="data-value">${profile?.nickname || '(미등록)'}</span>
          </div>
          <div class="data-item">
            <span class="data-label">지역</span>
            <span class="data-value">${normalizeRegion(profile?.region) || '(미등록)'}</span>
          </div>
          <div class="data-item">
            <span class="data-label">학교</span>
            <span class="data-value">${profile?.school || '(미등록)'}</span>
          </div>
        </div>
      </div>
    `;

    // 간단한 alert 대신 커스텀 모달 표시
    const dataModal = document.createElement('div');
    dataModal.className = 'modal-overlay data-modal active';
    dataModal.innerHTML = `
      <div class="modal-content data-content">
        <div class="modal-header">
          <h3>개인정보 열람</h3>
          <button class="modal-close" id="data-modal-close">&times;</button>
        </div>
        <div class="modal-body">
          ${dataHtml}
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" id="data-modal-confirm">확인</button>
        </div>
      </div>
    `;

    document.body.appendChild(dataModal);

    const closeDataModal = () => dataModal.remove();
    dataModal.querySelector('#data-modal-close').addEventListener('click', closeDataModal);
    dataModal.querySelector('#data-modal-confirm').addEventListener('click', closeDataModal);
    dataModal.addEventListener('click', (e) => {
      if (e.target === dataModal) closeDataModal();
    });
  }

  /**
   * 회원 탈퇴 (동의 철회) 처리
   *
   * 1단계: 비밀번호 재입력 모달 — 탈퇴로 사라지는 것을 보여 주고 본인 확인
   * 2단계: 마지막 확인창 → 삭제 실행
   */
  handleWithdrawConsent() {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay member-delete-modal active';
    overlay.innerHTML = `
      <div class="modal-content member-delete-content">
        <div class="modal-header">
          <h3>회원 탈퇴</h3>
          <button class="modal-close" data-close>&times;</button>
        </div>
        <div class="modal-body">
          <p class="member-delete-warning">
            탈퇴하면 다음 데이터가 <strong>영구 삭제</strong>되며 <strong>되돌릴 수 없습니다.</strong>
          </p>
          <ul class="member-delete-list">
            <li>계정(이메일) 및 프로필 정보</li>
            <li>저장된 프로젝트</li>
            <li>게시한 스토리맵 (공개 주소에서 더 이상 열리지 않음)</li>
            <li>모든 개인정보</li>
          </ul>
          <div class="form-group">
            <label for="withdraw-pw">비밀번호 확인</label>
            <input type="password" id="withdraw-pw" placeholder="현재 비밀번호를 입력하세요" autocomplete="current-password">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" data-close>취소</button>
          <button class="btn btn-danger" id="withdraw-confirm">탈퇴하기</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', close));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    const pwInput = overlay.querySelector('#withdraw-pw');
    const confirmBtn = overlay.querySelector('#withdraw-confirm');
    setTimeout(() => pwInput.focus(), 0);

    const resetButton = () => {
      confirmBtn.disabled = false;
      confirmBtn.textContent = '탈퇴하기';
    };

    const doWithdraw = async () => {
      const pw = pwInput.value;
      if (!pw) {
        alert('비밀번호를 입력하세요.');
        return;
      }

      confirmBtn.disabled = true;
      confirmBtn.textContent = '확인 중...';
      try {
        const ok = await supabaseManager.verifyPassword(pw);
        if (!ok) {
          alert('비밀번호가 일치하지 않습니다.');
          resetButton();
          pwInput.value = '';
          pwInput.focus();
          return;
        }

        const confirmLast = confirm(
          '마지막 확인입니다.\n\n' +
          '회원 탈퇴를 진행하면 모든 데이터가 영구적으로 삭제됩니다.\n' +
          '정말로 진행하시겠습니까?'
        );
        if (!confirmLast) {
          resetButton();
          return;
        }

        confirmBtn.textContent = '삭제 중...';
        await consentManager.withdrawConsent();
        close();
        alert('회원 탈퇴가 완료되었습니다.\n이용해주셔서 감사합니다.');
        this.close();
        window.location.reload();
      } catch (error) {
        alert('회원 탈퇴 처리 중 오류가 발생했습니다: ' + error.message);
        resetButton();
      }
    };

    confirmBtn.addEventListener('click', doWithdraw);
    pwInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); doWithdraw(); }
    });
  }

  /**
   * 모달 닫기
   */
  close() {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
  }
}

export const myPagePanel = new MyPagePanel();
